<script setup>
import { computed, ref, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Auth from 'dashboard/api/auth';
import TfPopupShell from './TfPopupShell.vue';

const props = defineProps({
  show: { type: Boolean, default: true },
});
const emit = defineEmits(['close']);

const currentUser = useMapGetter('getCurrentUser');
const accountHint = computed(
  () => currentUser.value?.email || currentUser.value?.available_name || '—'
);

const senhaAtual = ref('');
const senhaNova = ref('');
const senhaNovaConf = ref('');
const saving = ref(false);
const err = ref('');

watch(
  () => props.show,
  open => {
    if (!open) return;
    senhaAtual.value = '';
    senhaNova.value = '';
    senhaNovaConf.value = '';
    err.value = '';
  },
  { immediate: true }
);

async function salvar() {
  err.value = '';
  const atual = senhaAtual.value.trim();
  const nova = senhaNova.value.trim();
  const conf = senhaNovaConf.value.trim();
  if (!atual || !nova || !conf) {
    err.value = 'Preencha todos os campos.';
    return;
  }
  if (nova.length < 8) {
    err.value = 'A nova senha precisa ter no mínimo 8 caracteres.';
    return;
  }
  if (nova !== conf) {
    err.value = 'A confirmação não confere com a nova senha.';
    return;
  }
  saving.value = true;
  try {
    await Auth.profilePasswordUpdate({
      currentPassword: atual,
      password: nova,
      passwordConfirmation: conf,
    });
    useAlert('Senha atualizada.');
    emit('close');
  } catch (e) {
    const msg =
      e?.response?.data?.message ||
      e?.response?.data?.error ||
      (Array.isArray(e?.response?.data?.errors) && e.response.data.errors[0]) ||
      'Não foi possível alterar a senha.';
    err.value = String(msg);
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <TfPopupShell label="Minha senha" @close="emit('close')">
    <div class="senha-body">
      <p class="eyebrow">Conta</p>
      <h2 class="title">Minha senha</h2>
      <p class="hint">Conta: {{ accountHint }}</p>
      <p class="hint muted">Qualquer membro pode trocar a própria senha aqui.</p>
      <form class="form" @submit.prevent="salvar">
        <label class="field">
          <span>Senha atual</span>
          <input
            v-model="senhaAtual"
            type="password"
            autocomplete="current-password"
            placeholder="Digite sua senha atual"
          />
        </label>
        <label class="field">
          <span>Nova senha</span>
          <input
            v-model="senhaNova"
            type="password"
            autocomplete="new-password"
            minlength="8"
            placeholder="Mínimo 8 caracteres"
          />
        </label>
        <label class="field">
          <span>Confirmar nova senha</span>
          <input
            v-model="senhaNovaConf"
            type="password"
            autocomplete="new-password"
            minlength="8"
            placeholder="Repita a nova senha"
          />
        </label>
        <p v-if="err" class="err">{{ err }}</p>
        <button type="submit" class="btn" :disabled="saving">
          {{ saving ? 'Salvando…' : 'Salvar senha' }}
        </button>
      </form>
    </div>
  </TfPopupShell>
</template>

<style scoped>
.senha-body {
  padding: 28px 28px 32px;
  max-width: 420px;
  margin: 0 auto;
  width: 100%;
}
.eyebrow {
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
}
.title {
  margin: 0 0 12px;
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
}
.hint {
  margin: 0 0 6px;
  font-size: 13px;
  color: #64748b;
}
.hint.muted {
  opacity: 0.9;
  margin-bottom: 18px;
}
.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}
.field input {
  height: 40px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  padding: 0 12px;
  font-size: 14px;
  font-weight: 400;
  color: #0f172a;
  background: #fff;
}
.field input:focus {
  outline: none;
  border-color: #94a3b8;
  box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.25);
}
.err {
  margin: 0;
  font-size: 13px;
  color: #b91c1c;
}
.btn {
  margin-top: 4px;
  height: 42px;
  border: 0;
  border-radius: 10px;
  background: #0f172a;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
}
.btn:disabled {
  opacity: 0.6;
  cursor: wait;
}
</style>
